import { useCallback, useEffect, useRef, type ReactElement, type RefObject } from "react";
import { Link } from "react-router";

/** ページを変えたら、一覧の先頭が見えるところへ合わせる（この余白だけ上に残す）。 */
const scrollMargin = 16;

/** 別のページへのボタン（リンクの形）。押したページを <c>onPick</c> で知らせる。 */
function PageLink({ to, page, label, onPick }: {
    readonly to: string;
    readonly page: number;
    readonly label: string;
    readonly onPick: (page: number) => void;
}): ReactElement {
    return (
        <Link className="btn-secondary btn-sm" to={to} onClick={() => { onPick(page); }}>
            {label}
        </Link>
    );
}

export interface PagerProps {
    /** 今のページ（1 から）。 */
    readonly page: number;
    readonly pageCount: number;
    /** 先へ進めるか（契約 1 件の利用者は「1 ページ分が埋まったか」でサーバが決める）。 */
    readonly hasNext: boolean;
    /** 全体の数の文言（「全 120 名」「全 120 件」）。 */
    readonly total: string;
    /** そのページの URL。<b>絞り込みと並び順を残す</b>のは呼ぶ側。 */
    readonly hrefOf: (page: number) => string;
    /** ページを変えたときに見えるところへ合わせる一覧の先頭。 */
    readonly top: RefObject<HTMLElement | null>;
    /** 外枠に付ける目印の属性（テストと E2E が探す。<c>data-user-pager</c> など）。 */
    readonly marker: Readonly<Record<`data-${string}`, string>>;
}

/**
 * 一覧のページ送り（管理センター。<b>何件ずつか・何ページあるかはサーバが決める</b>）。
 *
 * ページを変えたときは、ページの先頭へは飛ばさず一覧の先頭（<c>top</c>）が見えるところへ合わせる——
 * 次のページの 1 行目から読める。合わせるのは押したページが届いてから（届く前に動かすと、前のページの途中を見せる）。
 * 1 ページしかなければ出さない。
 */
export function Pager({ page, pageCount, hasNext, total, hrefOf, top, marker }: PagerProps): ReactElement | null {
    const wanted = useRef<number | null>(null);

    useEffect(() => {
        const element = top.current;
        if (wanted.current !== page || element === null) {
            return;
        }
        wanted.current = null;
        const y = element.getBoundingClientRect().top + window.scrollY - scrollMargin;
        window.scrollTo(0, Math.max(0, y));
    }, [page, top]);
    const pick = useCallback((picked: number) => {
        wanted.current = picked;
    }, []);

    if (pageCount <= 1) {
        return null;
    }

    return (
        <div className="mt-4 flex items-center justify-between gap-4" {...marker}>
            {page > 1
                ? <PageLink to={hrefOf(page - 1)} page={page - 1} label="前へ" onPick={pick} />
                : <button type="button" className="btn-secondary btn-sm" disabled>前へ</button>}
            <p className="text-sm text-slate-500">
                {`${String(page)} / ${String(pageCount)} ページ（${total}）`}
            </p>
            {hasNext
                ? <PageLink to={hrefOf(page + 1)} page={page + 1} label="次へ" onPick={pick} />
                : <button type="button" className="btn-secondary btn-sm" disabled>次へ</button>}
        </div>
    );
}
